import type { ConferenceOptions, Reservation, Supervisor, Task, Worker } from "~/packages/taskrouter";
import type { OutboundFlows, AccountConfigData } from "~/modules/config";
import { AnalyticsInstance, EVENTS, EventPayloads } from "~/modules/telemetry/Analytics";
import type { Cbm } from "~/modules/cbm";
import { buildRegionalHost } from "~/utils/regionUtil";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { ErrorCode, ErrorSeverity, FlexSdkErrorMetadata } from "~/modules/error";
import { Logger } from "~/modules/logger";
import { TaskParticipant } from "~/modules/participant/TaskParticipant";
import { throwFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";
import { ContextManager } from "~/modules/contextManager/ContextManager";

export type TerminalReservationEvent = "canceled" | "rejected" | "rescinded" | "timeout";
export const TERMINAL_RESERVATION_EVENTS: TerminalReservationEvent[] = ["canceled", "rejected", "rescinded", "timeout"];

export function isWorkerOffline(worker: Supervisor, taskrouterOfflineActivitySid?: string): boolean {
    const currentActivitySid = worker.activity.sid;
    return taskrouterOfflineActivitySid === currentActivitySid;
}

export function isOutboundCallingEnabled(outboundCallFlows: Record<string, OutboundFlows> | null): boolean | undefined {
    return Boolean(outboundCallFlows?.default?.enabled);
}

export function getDefaultQueueSid(outboundCallFlows: Record<string, OutboundFlows> | null): string | null | undefined {
    return outboundCallFlows?.default?.queue_sid;
}

export function getDefaultWorkflowSid(
    outboundCallFlows: Record<string, OutboundFlows> | null
): string | null | undefined {
    return outboundCallFlows?.default?.workflow_sid;
}

export function getDefaultCallerID(outboundCallFlows: Record<string, OutboundFlows> | null): string | null | undefined {
    return outboundCallFlows?.default?.caller_id;
}

export function isOutboundCallPending(): boolean {
    return false;
}

export function getReservationsByTask(worker: Worker, taskSid: string): Reservation[] {
    const reservations = worker?.reservations ? Array.from(worker.reservations.values()) : [];
    return reservations.filter((reservation) => reservation.task?.sid === taskSid);
}

export function isEmailTask(task: Task): boolean {
    return task.taskChannelUniqueName === "email";
}

export function getCallSidOfTask(worker: Worker, taskSid: string): string | null | undefined {
    if (worker?.reservations) {
        
        for (const [key, reservation] of worker.reservations.entries()) {
            if (reservation.task?.sid === taskSid) {
                return reservation.task.attributes.callSid as string | null | undefined;
            }
        }
    }

    
    return null;
}

export function isCallTask(task: Task): boolean {
    return !!task && task.taskChannelUniqueName === "voice";
}

export function isLiveCall(reservation: Reservation) {
    return isCallTask(reservation?.task) && reservation.status === "accepted";
}

export function hasVoiceTaskWithStatus(worker: Worker, status: string): boolean {
    return worker?.reservations
        ? Array.from(worker.reservations.values()).some(
              (reservation) => reservation.task && reservation.status === status && isCallTask(reservation.task)
          )
        : false;
}

export function getVoiceReservation(worker: Worker): Reservation | null {
    if (worker?.reservations) {
        return (
            Array.from(worker.reservations.values()).find((reservation) => {
                return isLiveCall(reservation);
            }) ?? null
        );
    }
    return null;
}

export function isOutboundCallTask(task: Task): boolean {
    return isCallTask(task) && !!task.routingTarget && task.attributes.direction === "outbound";
}

function isTaskAssignedToCurrentWorker(reservation: Reservation, worker: Worker): boolean {
    return Boolean(reservation.task && reservation.workerSid === worker.sid);
}

export function canHold(reservation: Reservation, worker: Worker): boolean {
    return isLiveCall(reservation) && isTaskAssignedToCurrentWorker(reservation, worker);
}











export const addCallContextParameter = (
    newContextParameter: string,
    contactUri: string,
    existingRecipient: string | undefined
) => {
    
    const recipientWithCallContext = !existingRecipient ? contactUri : existingRecipient;

    const separator = recipientWithCallContext.includes("?") ? "&" : "?";

    return `${recipientWithCallContext}${separator}${newContextParameter}`;
};

export const isSupervisorAlreadyMonitoringCallTask = async (task: Task, worker: Worker, cbm: Cbm) => {
    const supervisorSid = worker.sid;

    if (!supervisorSid) {
        return false;
    }

    const participants = await cbm.getParticipantsByTask(task);

    return !!participants?.find((participant) => participant?.routingProperties?.workerSid === supervisorSid);
};

export function getRawRecordingCallbackUrl(accountConfig: AccountConfigData, region: string): string {
    const defaultURL = `https://webhooks${buildRegionalHost(
        region
    )}.twilio.com/v1/Accounts/{accountSid}/Workspaces/{workspaceSid}/Tasks/{taskSid}/FlexRecordingWebhook`;
    const { callRecordingWebhookUrl } = accountConfig;

    return callRecordingWebhookUrl ?? defaultURL;
}

export function getConferenceRecordingConfiguration(
    accountConfig: AccountConfigData,
    region: string,
    taskSid: string,
    conferenceOptions: ConferenceOptions | undefined
): Partial<ConferenceOptions> {
    const shouldRecordConference = accountConfig.callRecordingEnabled || !!conferenceOptions?.conferenceRecord;
    if (!shouldRecordConference) {
        return {};
    }

    const rawCallbackUrl =
        conferenceOptions?.conferenceRecordingStatusCallback ?? getRawRecordingCallbackUrl(accountConfig, region);

    const callbackUrl = rawCallbackUrl
        .replace("{accountSid}", accountConfig.accountSid)
        .replace("{workspaceSid}", accountConfig.taskrouterWorkspaceSid)
        .replace("{taskSid}", taskSid);

    return {
        conferenceRecord: conferenceOptions?.conferenceRecord ?? true,
        conferenceRecordingStatusCallback: callbackUrl,
        conferenceRecordingStatusCallbackMethod: conferenceOptions?.conferenceRecordingStatusCallbackMethod ?? "POST"
    };
}

export interface CheckCommons<K extends EVENTS> {
    worker: Worker;
    actionName: string;
    moduleName: string;
    logger: Logger;
    context: ContextManager;
    event?: EVENTS;
    extra?: EventPayloads[K];
    analytics?: AnalyticsInstance;
}

export function checkReservationExists<K extends EVENTS>(
    reservation: Reservation | undefined | null,
    taskSid: string,
    checkUtils: CheckCommons<K>
) {
    if (!reservation) {
        const errorMsg = `${checkUtils.actionName}: Reservation for task ${taskSid} not found or has invalid status`;
        checkUtils.logger.error(errorMsg);
        if (checkUtils.event && checkUtils.analytics) {
            sendWorkerTrackingEvent(checkUtils.event, checkUtils.analytics, checkUtils.worker, {
                ...checkUtils.extra,
                error: errorMsg
            });
        }

        const metadata: FlexSdkErrorMetadata = {
            severity: ErrorSeverity.Error,
            module: checkUtils.moduleName,
            source: checkUtils.actionName
        };
        throwFlexSdkError(checkUtils.context)(ErrorCode.TaskReservationNotFound, metadata, errorMsg);
    }
}

export function checkRequiredParameter<K extends EVENTS>(
    parameter: unknown,
    parameterName: string,
    checkUtils: CheckCommons<K>
) {
    if (parameter === null || parameter === undefined || (typeof parameter === "string" && parameter.trim() === "")) {
        const errorMsg = `${checkUtils.actionName}: The parameter '${parameterName}' is required but has not been provided`;
        checkUtils.logger.error(errorMsg);

        if (checkUtils.event && checkUtils.analytics) {
            sendWorkerTrackingEvent(checkUtils.event, checkUtils.analytics, checkUtils.worker, {
                ...checkUtils.extra,
                error: errorMsg
            });
        }
        const metadata: FlexSdkErrorMetadata = {
            severity: ErrorSeverity.Error,
            module: checkUtils.moduleName,
            source: checkUtils.actionName
        };
        throwFlexSdkError(checkUtils.context)(ErrorCode.MissingRequiredParameter, metadata, errorMsg);
    }
}

export function checkInteractionSid<K extends EVENTS>(
    interactionSid: string | undefined,
    taskSid: string,
    checkUtils: CheckCommons<K>
) {
    if (!interactionSid) {
        const errorMsg = `${checkUtils.actionName}: Missing interactionSid for task ${taskSid}`;
        checkUtils.logger.error(errorMsg);
        if (checkUtils.event && checkUtils.analytics) {
            sendWorkerTrackingEvent(checkUtils.event, checkUtils.analytics, checkUtils.worker, {
                ...checkUtils.extra,
                error: errorMsg
            });
        }
        const metadata: FlexSdkErrorMetadata = {
            severity: ErrorSeverity.Error,
            module: checkUtils.moduleName,
            source: checkUtils.actionName
        };
        throwFlexSdkError(checkUtils.context)(ErrorCode.MissingTaskInteractionSid, metadata, errorMsg);
    }
}

export function checkChannelSid<K extends EVENTS>(
    channelSid: string | undefined,
    taskSid: string,
    checkUtils: CheckCommons<K>
) {
    if (!channelSid) {
        const errorMsg = `${checkUtils.actionName}: Missing channelSid for task ${taskSid}`;
        checkUtils.logger.error(errorMsg);
        if (checkUtils.event && checkUtils.analytics) {
            sendWorkerTrackingEvent(checkUtils.event, checkUtils.analytics, checkUtils.worker, {
                ...checkUtils.extra,
                error: errorMsg
            });
        }
        const metadata: FlexSdkErrorMetadata = {
            severity: ErrorSeverity.Error,
            module: checkUtils.moduleName,
            source: checkUtils.actionName
        };
        throwFlexSdkError(checkUtils.context)(ErrorCode.MissingChannelSid, metadata, errorMsg);
    }
}

export async function handleReservationAcceptance<K extends EVENTS>(
    cbm: Cbm,
    reservation: Reservation,
    checkUtils: CheckCommons<K>
): Promise<Reservation> {
    try {
        return await cbm.acceptReservation(reservation);
    } catch (error) {
        const errorMsg = `${checkUtils.actionName}: Failed to accept reservation ${reservation.sid}`;
        checkUtils.logger.error(errorMsg, error);

        if (checkUtils.event && checkUtils.analytics) {
            sendWorkerTrackingEvent(checkUtils.event, checkUtils.analytics, checkUtils.worker, {
                ...checkUtils.extra,
                error: `${errorMsg} (${toCauseMessage(error)})`
            });
        }
        const metadata: FlexSdkErrorMetadata = {
            severity: ErrorSeverity.Error,
            module: checkUtils.moduleName,
            source: checkUtils.actionName
        };
        return throwFlexSdkError(checkUtils.context)(ErrorCode.FailedToAcceptReservation, metadata, errorMsg, error);
    }
}

export function checkFlexInstanceSid<K extends EVENTS>(
    instanceSid: string | undefined | null,
    checkUtils: CheckCommons<K>
): string {
    if (!instanceSid) {
        const errorMsg = `${checkUtils.actionName}: Failed to get Flex instance SID from account configuration`;
        checkUtils.logger.error(errorMsg);
        if (checkUtils.event && checkUtils.analytics) {
            sendWorkerTrackingEvent(checkUtils.event, checkUtils.analytics, checkUtils.worker, {
                ...checkUtils.extra,
                error: errorMsg
            });
        }
        const metadata: FlexSdkErrorMetadata = {
            severity: ErrorSeverity.Error,
            module: checkUtils.moduleName,
            source: checkUtils.actionName
        };
        return throwFlexSdkError(checkUtils.context)(ErrorCode.FailedToGetFlexInstanceSid, metadata, errorMsg);
    }
    return instanceSid;
}

export async function handleGetParticipantsByTask<K extends EVENTS>(
    cbm: Cbm,
    task: Task,
    checkUtils: CheckCommons<K>,
    getParticipantOptions?: object
): Promise<TaskParticipant[]> {
    try {
        const participants = await cbm.getParticipantsByTask(task, getParticipantOptions);
        return participants;
    } catch (error) {
        const errorMsg = `${checkUtils.actionName}: Failed to retrieve participants for task ${task.sid}`;
        checkUtils.logger.error(errorMsg, error);

        if (checkUtils.event && checkUtils.analytics) {
            sendWorkerTrackingEvent(checkUtils.event, checkUtils.analytics, checkUtils.worker, {
                ...checkUtils.extra,
                error: `${errorMsg} (${toCauseMessage(error)})`
            });
        }
        const metadata: FlexSdkErrorMetadata = {
            severity: ErrorSeverity.Error,
            module: checkUtils.moduleName,
            source: checkUtils.actionName
        };
        return throwFlexSdkError(checkUtils.context)(
            ErrorCode.FailedToRetrieveTaskParticipants,
            metadata,
            errorMsg,
            error
        );
    }
}

export function checkWorker<K extends EVENTS>(worker: Worker | undefined, checkUtils: CheckCommons<K>) {
    if (!worker) {
        const errorMsg = `${checkUtils.actionName}: Worker is not initialized`;
        checkUtils.logger.error(errorMsg);

        if (checkUtils.event && checkUtils.analytics) {
            sendWorkerTrackingEvent(checkUtils.event, checkUtils.analytics, checkUtils.worker, {
                ...checkUtils.extra,
                error: errorMsg
            });
        }

        const metadata: FlexSdkErrorMetadata = {
            severity: ErrorSeverity.Error,
            module: checkUtils.moduleName,
            source: checkUtils.actionName
        };
        throwFlexSdkError(checkUtils.context)(ErrorCode.WorkerNotInitialized, metadata, errorMsg);
    }
}

export function checkParticipantExists<K extends EVENTS>(
    participant: TaskParticipant | undefined,
    participantSid: string,
    taskSid: string,
    checkUtils: CheckCommons<K>
) {
    if (!participant) {
        const errorMsg = `${checkUtils.actionName}: Participant ${participantSid} not found in task ${taskSid}`;
        checkUtils.logger.error(errorMsg);

        if (checkUtils.event && checkUtils.analytics) {
            sendWorkerTrackingEvent(checkUtils.event, checkUtils.analytics, checkUtils.worker, {
                ...checkUtils.extra,
                error: errorMsg
            });
        }
        const metadata = {
            severity: ErrorSeverity.Error,
            module: checkUtils.moduleName,
            source: checkUtils.actionName
        };
        throwFlexSdkError(checkUtils.context)(ErrorCode.ParticipantNotFound, metadata, errorMsg);
    }
}

export function checkIsCallTask<K extends EVENTS>(task: Task | undefined, checkUtils: CheckCommons<K>) {
    if (!task || !isCallTask(task)) {
        const errorMsg = `${checkUtils.actionName}: Task ${task?.sid} is not a voice task`;
        checkUtils.logger.error(errorMsg);

        if (checkUtils.event && checkUtils.analytics) {
            sendWorkerTrackingEvent(checkUtils.event, checkUtils.analytics, checkUtils.worker, {
                ...checkUtils.extra,
                error: errorMsg
            });
        }
        const metadata = {
            severity: ErrorSeverity.Error,
            module: checkUtils.moduleName,
            source: checkUtils.actionName
        };
        throwFlexSdkError(checkUtils.context)(ErrorCode.TaskNotVoiceTask, metadata, errorMsg);
    }
}

export function findSupervisorOnCall<K extends EVENTS>(
    participants: TaskParticipant[],
    taskSid: string,
    checkUtils: CheckCommons<K>
): TaskParticipant {
    const supervisor = participants.find(
        (p) => p.routingProperties?.workerSid === checkUtils.worker.sid && String(p.type) === "supervisor"
    );
    if (!supervisor) {
        const errorMsg = `${checkUtils.actionName}: Supervisor is not on the call for task ${taskSid}`;
        checkUtils.logger.error(errorMsg);
        if (checkUtils.event && checkUtils.analytics) {
            sendWorkerTrackingEvent(checkUtils.event, checkUtils.analytics, checkUtils.worker, {
                ...checkUtils.extra,
                error: errorMsg,
                taskSid
            });
        }
        throwFlexSdkError(checkUtils.context)(
            ErrorCode.SupervisorNotOnCall,
            { severity: ErrorSeverity.Error },
            errorMsg
        );

        
        return null as unknown as TaskParticipant;
    }
    return supervisor;
}
